import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { UserPlus, Shield, User } from 'lucide-react';
import { motion } from 'framer-motion';

const FamilyManager = () => {
    const { token, user } = useAuth();
    const [members, setMembers] = useState([]);
    const [householdName, setHouseholdName] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const res = await axios.get('http://localhost:4000/api/auth/members', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMembers(res.data.data.members);
                setHouseholdName(res.data.data.household);
            } catch (error) {
                console.error("Failed to fetch members", error);
            } finally {
                setLoading(false);
            }
        };
        fetchMembers();
    }, [token]);

    return (
        <Layout>
            <div className="max-w-7xl mx-auto space-y-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{householdName || 'My Family'}</h1>
                        <p className="text-gray-500">Manage your household members and access permissions.</p>
                    </div>
                    <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-lg">
                        <UserPlus className="w-5 h-5 mr-2" />
                        Invite Member
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div></div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {members.map((member, idx) => (
                            <motion.div 
                                key={member._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 flex items-center space-x-4"
                            >
                                <div className={`h-16 w-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md bg-gradient-to-br ${member.role === 'ADMIN' ? 'from-purple-500 to-indigo-600' : 'from-blue-400 to-cyan-500'}`}>
                                    {member.email[0].toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 dark:text-white truncate max-w-[150px]" title={member.email}>{member.email.split('@')[0]}</h3>
                                    <div className="flex items-center mt-1 space-x-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${member.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                            {member.role === 'ADMIN' ? <Shield className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                                            {member.role}
                                        </span>
                                        {member._id === user?.id && <span className="text-xs text-gray-400">(You)</span>}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-2">Joined {new Date(member.created_at).toLocaleDateString()}</p>
                                </div>
                            </motion.div>
                        ))}
                        
                        {/* Add Member Placeholder Card */}
                        <motion.div 
                            whileHover={{ scale: 1.02 }}
                            className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-gray-800 transition-all group"
                        >
                            <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors mb-3">
                                <UserPlus className="w-6 h-6" />
                            </div>
                            <h3 className="font-semibold text-gray-500 group-hover:text-blue-600">Add Family Member</h3>
                            <p className="text-xs text-gray-400 mt-1">Share documents safely</p>
                        </motion.div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FamilyManager;
